<template>
  <app-confirm-dialog
    v-model="dialog"
    @confirm="confirm"
    @cancel="cancel"
    maxWidth="320"
    confirmBtnText="開始計費輔導"
    cancelBtnText="拒絕邀請"
    :loading="loading"
    :showConfirmBtn="showConfirmBtn"
    :showCancelBtn="showCancelBtn">
    <div class="teacher-info">
      <app-avatar :id="teacherAvatarID" :size="128"/>
      <span class="title pt-4">{{teacherName}} 老師</span>
    </div>
    <div class="pt-4 subheading">
      <span>{{dialogText}}</span>
    </div>
    <v-progress-linear
      v-if="countdownProgress"
      v-model="countdownPercentage"
      color="secondary" />
  </app-confirm-dialog>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import {FudaoEvent, fudaoAppService} from 'langyan';

const getDefaultData = () => {
  return {
    loading: false,
    dialog: false,
    dialogText: '',
    showConfirmBtn: true,
    showCancelBtn: true,
    waitingTime: 0,
    fudaoDto: {},
    countdownProgress: true
  };
};

export default {
  data: getDefaultData,
  computed: {
    ...mapState({
      userid: state => state.userid
    }),

    ...mapState('fudao', {
      responseTimeout: state => state.responseTimeout
    }),

    teacherAvatarID() {
      return this.fudaoDto.teacher ? this.fudaoDto.teacher.Avatar : null;
    },

    teacherName() {
      let teacher = this.fudaoDto.teacher;
      if (!teacher) {
        return '';
      }
      return teacher.DisplayName || teacher.Name;
    },

    isSelfInvite() {
      return this.fudaoDto.teacher.Id === this.userid;
    },

    countdownPercentage() {
      return Math.round(this.waitingTime/(this.responseTimeout/1000) * 100);
    }
  },

  watch: {
    dialog(value) {
      if (!value) {
        this.reset();
      }
    },

    waitingTime(value) {
      if (value < this.responseTimeout/1000) {
        return;
      }

      this.stopCountdownProgress();
      this.sentNoResponse();
    }
  },

  mounted() {
    this.$eventBus.$on(FudaoEvent.StudentStartInvitationReceived, this.onStudentStartInvitationReceived);
  },

  beforeDestroy() {
    this.$eventBus.$off(FudaoEvent.StudentStartInvitationReceived, this.onStudentStartInvitationReceived);
    this.reset();
  },

  methods: {
    ...mapActions('alert', {
      addAlert: 'add'
    }),

    isDifferentFudao(domainEvent) {
      return this.fudaoDto.id && this.fudaoDto.id !== domainEvent.body.id;
    },

    stopCountdownProgress() {
      clearInterval(this._countdownTimeInterval);
      this.countdownProgress = false;
    },

    open() {
      this.dialog = true;
    },

    reset() {
      this.$eventBus.$off(FudaoEvent.TeacherStartInvitationCanceled, this.onTeacherStartInvitationCanceled);
      this.$eventBus.$off(FudaoEvent.StudentStartInvitationNoResponse, this.onStudentStartInvitationNoResponse);
      this.$eventBus.$off(FudaoEvent.FudaoStarted, this.onFudaoStarted);
      this.$eventBus.$off(FudaoEvent.FudaoStartFailed, this.onFudaoStartFailed);

      this.stopCountdownProgress();
      let data = getDefaultData();
      Object.keys(data).forEach(key => this[key] = data[key]);
    },

    async confirm() {
      if (!this.isStudent || this.fudaoDto.teacher.Id === this.userid) {
        this.addAlert({level: 'error', content: '無法接受輔導邀請: 角色錯誤'});
        this.dialog = false;
        return;
      }
      this.stopCountdownProgress();
      this.loading = true;
      this.showCancelBtn = false;
      await fudaoAppService.acceptFudaoAsync(this.fudaoDto.id);
    },

    async cancel() {
      this.stopCountdownProgress();
      await fudaoAppService.rejectFudaoAsync(this.fudaoDto.id);
      this.dialog = false;
    },

    sentNoResponse() {
      fudaoAppService.noResponseFudaoAsync(this.fudaoDto.id);
    },

    // events ------------------------------------------------------

    onTeacherStartInvitationCanceled(domainEvent) {
      if (this.isSelfInvite || this.isDifferentFudao(domainEvent)) {
        return;
      }
      this.addAlert({level: 'info', content: '老師取消邀請輔導'});
      this.dialog = false;
    },

    onStudentStartInvitationReceived(domainEvent) {
      this.$eventBus.$on(FudaoEvent.TeacherStartInvitationCanceled, this.onTeacherStartInvitationCanceled);
      this.$eventBus.$on(FudaoEvent.StudentStartInvitationNoResponse, this.onStudentStartInvitationNoResponse);
      this.$eventBus.$on(FudaoEvent.FudaoStarted, this.onFudaoStarted);
      this.$eventBus.$on(FudaoEvent.FudaoStartFailed, this.onFudaoStartFailed);

      this.fudaoDto = domainEvent.body;
      this.dialogText = '向你發送輔導邀請，是否進入計費輔導?';
      this.dialog = true;

      this._countdownTimeInterval = setInterval(() => {
        this.waitingTime++;
      }, 1000);
    },

    onStudentStartInvitationNoResponse(domainEvent) {
      if (this.isSelfInvite || this.isDifferentFudao(domainEvent)) {
        return;
      }
      this.addAlert({level: 'warning', content: '回應逾時'});
      this.dialog = false;
    },

    onFudaoStarted(domainEvent) {
      this.dialog = false;
    },

    onFudaoStartFailed(errMsg) {
      this.addAlert({level: 'error', content: '邀請輔導發生錯誤: ' + errMsg});
    }
  }
};
</script>

<style lang="stylus" scoped>
.teacher-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
}
</style>
